import { useCallback, useEffect } from 'react';

import {
  SmsReminderDryRunQuery,
  SmsReminderPlanFieldsFragment,
  SmsReminderPlansDocument,
  SmsReminderPlansQuery,
  useSmsReminderDryRunQuery,
  useSmsReminderPlansQuery,
  useStoreSmsReminderPlanMutation,
} from 'core/graphql';

import { ISmsError, smsCrashErrors, toSmsErrors } from './smsFeedback';
import { ISmsPlanStepInput, SMS_DRY_RUN_LIMIT } from './smsModel';
import { useSmsPermissions } from './useSmsPermissions';

/**
 * ТЗ 0081 — лесенки автонапоминаний (подпись договора, оплата фактуры,
 * лиды): шаги, включение и сухой прогон «что робот отправил бы сейчас».
 */

export type ISmsReminderPlan = SmsReminderPlanFieldsFragment;
export type ISmsReminderPreviewRow = SmsReminderDryRunQuery['previewSmsReminders'][number];

const EMPTY_PLANS: ISmsReminderPlan[] = [];
const EMPTY_ROWS: ISmsReminderPreviewRow[] = [];

export const useSmsReminderPlans = () => {
  const { canUse } = useSmsPermissions();
  const { data, loading, error, refetch } = useSmsReminderPlansQuery({
    skip: !canUse,
    fetchPolicy: 'cache-and-network',
  });
  const [saveMut, { loading: saving }] = useStoreSmsReminderPlanMutation();

  // Перепроверка 0084: «Изменить текст» открывает шаблон в новой вкладке.
  // Вернулся на вкладку — лесенка перечитывается: включённый или
  // переименованный там шаблон виден сразу, без перезагрузки. Окно `focus`,
  // а не `visibilitychange`: у фонового окна `visibilityState` уже
  // `hidden`, и возврат он не ловит.
  useEffect(() => {
    if (!canUse) return undefined;
    const onFocus = () => {
      refetch().catch(() => undefined);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [canUse, refetch]);

  /**
   * Сохранить лесенку целиком. У `ReminderPlanType` нет `id` — ответ
   * подменяет свою лесенку в списке руками, по теме.
   */
  const save = useCallback(
    async (
      topic: string,
      enabled: boolean,
      steps: ISmsPlanStepInput[],
    ): Promise<{ ok: boolean; errors: ISmsError[] }> => {
      try {
        const { data: answer } = await saveMut({
          variables: { topic, enabled, steps },
          update: (cache, { data: result }) => {
            const plan = result?.saveReminderPlan?.plan;
            if (!plan) return;
            const current = cache.readQuery<SmsReminderPlansQuery>({
              query: SmsReminderPlansDocument,
            });
            if (!current) return;
            cache.writeQuery<SmsReminderPlansQuery>({
              query: SmsReminderPlansDocument,
              data: {
                reminderPlans: current.reminderPlans.map((item) =>
                  item.topic === plan.topic ? plan : item,
                ),
              },
            });
          },
        });
        const payload = answer?.saveReminderPlan;
        const errors = toSmsErrors(payload?.customErrors);
        return { ok: Boolean(payload?.success) && !errors.length, errors };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught) };
      }
    },
    [saveMut],
  );

  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    plans: data?.reminderPlans ?? EMPTY_PLANS,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
    save,
    saving,
  };
};

/**
 * Сухой прогон лесенки. Всегда с сервера (`network-only`): смысл кнопки —
 * «что сейчас», а не что было при прошлом открытии.
 */
export const useSmsReminderDryRun = (topic: string | null) => {
  const { canUse } = useSmsPermissions();
  const { data, loading, error, refetch } = useSmsReminderDryRunQuery({
    variables: { topic: topic || '', limit: SMS_DRY_RUN_LIMIT },
    skip: !canUse || !topic,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    rows: data?.previewSmsReminders ?? EMPTY_ROWS,
    loading,
    failed: Boolean(error),
    reload,
  };
};
